import { createContext, useCallback, JSX } from 'react';

import PropTypes from 'prop-types';
import { Outlet } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useIdentity } from '@zenbusiness/application-commons-identity';

import {
  LOCAL_STORAGE_FLOW_STATES_KEY,
  LOCAL_STORAGE_FLOW_STATE_DEFAULT
} from '#client/constants/localStorage';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useSaveFlowState from '#client/hooks/useSaveFlowState';
import setter from '#client/utils/setter';
/** @typedef {import('react')} React */

/**
 * @typedef {object} FlowStateContextValue
 * @property {object} contactInfo - The contact information.
 * @property {object} businessInfo - The business information.
 * @property {string} lastPage - The last page (progress) in this flow from the most recent session.
 * @property {string} configHash - The flow config hash from the most recent session.
 * @property {string} cartUuid - The cart UUID from the most recent session.
 * @property {string} accountUuid - The account UUID created during this flow.
 * @property {string} orderUuid - The order UUID last associated with this flow (if one was placed).
 * @property {function(string): any} get - Function to get a value from the flow state.
 * @property {function(string, any | Function): void} set - Function to set a value in the flow state.
 */

/** @type {React.Context<FlowStateContextValue>} */
export const FlowStateContext = createContext();

/**
 * FlowStateProvider component to provide the flow state context to its children.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The children components.
 * @param {string} props.flowId The id of the flow
 * @returns {JSX.Element} The provider component.
 */
export function FlowStateProvider({ flowId, children }) {
  const { hash, entityType: flowEntityType } = useFlowConfig() ?? {};
  const { identified, user: userIdentity } = useIdentity();
  const { businessEntity } = useBusinessEntity();
  const [flowStates, setFlowStates, getFlowStates] = useLocalStorage(
    LOCAL_STORAGE_FLOW_STATES_KEY,
    {},
    { isJSON: true }
  );

  const { saveFlowState, cancelSaveFlowState } = useSaveFlowState(flowId);

  const flowState = flowStates[flowId] ?? {};
  const isFreshState = Object.keys(flowState).length === 0;
  let { businessInfo, contactInfo, trackingParams } = flowState;
  const { configHash, lastPage, cartUuid, accountUuid, orderUuid } = flowState;
  const get = useCallback((key) => getFlowStates()[flowId]?.[key], [flowId, getFlowStates]);
  const set = useCallback(
    (key, valueOrUpdater) => {
      const allStates = getFlowStates();
      const currentFlowState = allStates[flowId];
      allStates[flowId] = setter(currentFlowState, key, valueOrUpdater);
      setFlowStates({ ...allStates });
      saveFlowState(allStates[flowId]);
    },
    [setFlowStates, getFlowStates, flowId, saveFlowState]
  );

  const clear = useCallback(() => {
    // cancel saveFlowState when clear is called to avoid save an empty state and a re-render that causes flow to go back to first page
    cancelSaveFlowState();
    const allStates = getFlowStates();
    delete allStates[flowId];
    setFlowStates(allStates);
  }, [setFlowStates, getFlowStates, flowId, cancelSaveFlowState]);

  // if this is a fresh state then persist a default value
  if (isFreshState) {
    const newState = { ...LOCAL_STORAGE_FLOW_STATE_DEFAULT };
    newState.configHash = hash ?? null;
    newState.businessInfo.entityType = flowEntityType ?? null;
    flowStates[flowId] = newState;
    setFlowStates(flowStates);
  }

  // if there is a BE selected, override what we have in the flow state
  if (businessEntity?.uuid) {
    businessInfo = {
      ...LOCAL_STORAGE_FLOW_STATE_DEFAULT.businessInfo,
      businessEntityUuid: businessEntity.uuid,
      entityType: businessEntity.type ?? flowEntityType,
      jurisdictionState: businessEntity.domesticJurisdiction,
      name: businessEntity.name
    };
  }

  // if there is an identity, override what we have in the flow state
  if (identified) {
    const { firstName, lastName, email } = userIdentity;
    contactInfo = {
      ...LOCAL_STORAGE_FLOW_STATE_DEFAULT.contactInfo,
      firstName,
      lastName,
      email
    };
  }

  const stateContext = useMemoObject({
    businessInfo,
    contactInfo,
    lastPage,
    configHash,
    cartUuid,
    accountUuid,
    orderUuid,
    trackingParams,
    get,
    set,
    clear
  });

  return (
    <FlowStateContext.Provider value={stateContext}>
      {children || <Outlet />}
    </FlowStateContext.Provider>
  );
}

FlowStateProvider.propTypes = {
  flowId: PropTypes.string.isRequired
};
